import { useQueryManager } from "@/composables/useQueryManager";
import {
  classifyActiveFilters,
  formatFilterChip,
  summarizeActiveFilters,
  trailingFilterAction,
  type DocumentFilterState,
} from "@/dashboard/inventory_management/common/portfolioFilterChips";
import {
  buildFilterOptions,
  type FilterOptionLabels,
} from "@/dashboard/inventory_management/common/portfolioFilterOptions";
import {
  activeCodAgeConditions,
  type SelectableFilterColumn,
} from "@/dashboard/inventory_management/common/portfolioTableColumns";
import {
  cloneFilterValue,
  dataTableFilterValue,
  emptyFilterValue,
  FILTER_COLUMN_KEYS,
  filterValuesEqual,
  isFilterValueActive,
  toDataTableFilters,
  type DataTableFilters,
  type FilterColumn,
  type FilterOption,
  type FilterState,
  type FilterValue,
} from "@/dashboard/inventory_management/common/portfolioTableFilters";
import {
  decodeTableFilterState,
  encodeTableFilterState,
  restoreStoredFilterValue,
} from "@/dashboard/inventory_management/common/portfolioTableFilterUrlState";
import type { FlattenedPortfolioItem } from "@/types";
import type { TranslationFn } from "@/utils/countries";
import { debounce, isEqual } from "lodash-es";
import type { DataTableFilterMeta } from "primevue/datatable";
import {
  computed,
  onScopeDispose,
  reactive,
  ref,
  shallowRef,
  watch,
} from "vue";

/**
 * How long the URL write waits. Text filters would otherwise push a route per keystroke, and the
 * wait lets the write read a route that already carries a selection change made in the same tick
 * (both go through `updateQuery`, which merges into the current query).
 */
export const FILTER_URL_WRITE_DELAY_MS = 250;

export interface PortfolioTableFiltersConfig {
  /** The rows the table renders: the options, and the defaults resolved against them, follow these. */
  items: () => readonly FlattenedPortfolioItem[];
  labels: () => FilterOptionLabels;
  t: TranslationFn;
  /** The portfolio_type include-list the column starts at. */
  defaultPortfolioTypes: () => readonly string[] | undefined;
  /** Values each column hides by default, among those present in the items. */
  excludedByDefault: () =>
    Partial<Record<SelectableFilterColumn, string[]>> | undefined;
  /** Persist the overrides in this query param, stamped with the scope (e.g. the company ids). */
  persist?: { param: string; scope: () => string };
}

/** A user's change to a column. `null` clears a column that has a default. */
type FilterOverride<C extends FilterColumn> = FilterValue<C> | null;

type FilterOverrides = { [C in FilterColumn]?: FilterOverride<C> };

/**
 * The PortfolioItemsTable's filter state. Only what the user changed is stored: every column's value
 * is its override, else its default, else empty, so a default that re-resolves after a refetch moves
 * every column still at it without any write.
 *
 * `primeFilters` is the DataTable's `filters`, and `applyPrimeFilters` takes its `update:filters`. Each
 * read hands out fresh copies, because the editors edit the DataTable's copy of a value in place (the
 * COD age editor) and the DataTable returns that copy on every `filterCallback`; applying stores each
 * column that differs from its value.
 *
 * A document's filters (`setDocument`) tag their chips "From document" while a column still holds
 * the document's value; editing a column drops its tag and makes the filter the user's own.
 *
 * With `persist`, the overrides are restored from the URL on setup and whenever the scope changes
 * (filters stored for another scope are ignored, list values no longer among the options dropped),
 * and written back debounced.
 */
export function usePortfolioTableFilters(config: PortfolioTableFiltersConfig) {
  const options = computed(() =>
    buildFilterOptions(config.items(), config.labels(), config.t)
  );

  // PERF: options rebuild on every refetch and locale switch. Keeping an equal previous result keeps
  // `values` and with it the DataTable's filters, which would otherwise re-filter every row and reset
  // the scroll.
  const defaults = computed((previous?: Partial<FilterState>) => {
    const resolved: Partial<FilterState> = {};
    const excludedByColumn = Object.entries(
      config.excludedByDefault() ?? {}
    ) as [SelectableFilterColumn, string[]][];
    for (const [column, excluded] of excludedByColumn) {
      const present = (optionsOf(column) ?? []).map((option) => option.value);
      const hidden = present.filter((value) =>
        excluded.includes(String(value))
      );
      if (hidden.length > 0) {
        resolved[column] = present.filter((value) => !hidden.includes(value));
      }
    }
    const portfolioTypes = config.defaultPortfolioTypes();
    if (portfolioTypes) resolved.portfolio_type = [...portfolioTypes];
    return keepIfEqual(resolved, previous);
  });

  // Replaced, never mutated: every write builds a new object, in column order.
  const overrides = shallowRef<FilterOverrides>({});

  // A document's COD years stay offered while the user tries other ones; any other programmatic
  // COD write (clear, reset, restore) forgets them.
  const importedCodAgeYears = ref<number[]>([]);

  const documentFilters = shallowRef<DocumentFilterState | null>(null);

  const valueOf = <C extends FilterColumn>(column: C): FilterValue<C> => {
    const override = overrides.value[column];
    if (override === null) return emptyFilterValue(column);
    return override ?? defaults.value[column] ?? emptyFilterValue(column);
  };

  // A column with a default is at it when equal by value; one without is at it only while its value
  // is the empty value itself, so a COD condition added without a year yet stays in the editor.
  const isAtDefault = <C extends FilterColumn>(
    column: C,
    value: FilterValue<C>
  ): boolean =>
    column in defaults.value
      ? filterValuesEqual(column, value, defaults.value[column])
      : isEqual(value, emptyFilterValue(column));

  /** What a column stores for this value: nothing at its default, `null` for a cleared default. */
  const overrideFor = <C extends FilterColumn>(
    column: C,
    value: FilterValue<C> | null
  ): FilterOverride<C> | undefined => {
    const resolved = value ?? emptyFilterValue(column);
    if (isAtDefault(column, resolved)) return undefined;
    if (column in defaults.value && !isFilterValueActive(column, resolved)) {
      return null;
    }
    return cloneFilterValue(column, resolved);
  };

  /**
   * `base` with each column `next` gives a value (`null` clears it) set to that value's override;
   * a column it gives `undefined` keeps its override from `base`.
   */
  const overridesWith = (
    base: FilterOverrides,
    next: (column: FilterColumn) => FilterValue | null | undefined
  ): FilterOverrides =>
    // Object.fromEntries can't carry each column's own value type.
    Object.fromEntries(
      FILTER_COLUMN_KEYS.flatMap((column) => {
        const value = next(column);
        const override =
          value === undefined ? base[column] : overrideFor(column, value);
        return override === undefined ? [] : [[column, override]];
      })
    ) as FilterOverrides;

  // PERF: a new overrides object hands the DataTable new filters, which re-filters every row and
  // resets the scroll; a write that changes nothing (e.g. clearing columns already empty) keeps it.
  const writeOverrides = (next: FilterOverrides) => {
    if (!isEqual(next, overrides.value)) overrides.value = next;
  };

  // Text and COD columns have no options.
  function optionsOf(column: FilterColumn): FilterOption[] | undefined {
    const byColumn: Partial<Record<FilterColumn, FilterOption[]>> =
      options.value;
    return byColumn[column];
  }

  // Keyed in column order, which the URL encoding follows.
  const values = computed((previous?: FilterState) =>
    keepIfEqual(
      // Object.fromEntries can't carry each column's own value type.
      Object.fromEntries(
        FILTER_COLUMN_KEYS.map((column) => [column, valueOf(column)])
      ) as FilterState,
      previous
    )
  );

  const primeFilters = computed<DataTableFilters>(() =>
    toDataTableFilters(values.value)
  );

  /** Stores the columns the DataTable's edited `filters` model changed; an unreadable column is left as is. */
  const applyPrimeFilters = (edited: DataTableFilterMeta) => {
    const changed = FILTER_COLUMN_KEYS.filter((column) => {
      const value = dataTableFilterValue(edited, column);
      return value !== undefined && !isEqual(value, values.value[column]);
    });
    if (changed.length === 0) return;
    overrides.value = overridesWith(overrides.value, (column) =>
      changed.includes(column)
        ? dataTableFilterValue(edited, column)
        : undefined
    );
  };

  const activeFilters = computed(() =>
    classifyActiveFilters({
      values: values.value,
      defaults: defaults.value,
      document: documentFilters.value,
    })
  );

  const summary = computed(() => summarizeActiveFilters(activeFilters.value));

  const chips = computed(() =>
    activeFilters.value.map((filter) =>
      formatFilterChip(filter, { options: options.value, t: config.t })
    )
  );

  const trailingAction = computed(() =>
    trailingFilterAction(values.value, defaults.value)
  );

  const codAgeYearOptions = computed(() => {
    const years = new Set([
      ...Array.from({ length: 15 }, (_, i) => i + 1),
      ...importedCodAgeYears.value,
    ]);
    for (const condition of activeCodAgeConditions(values.value.cod_age)) {
      years.add(condition.value);
    }
    return [...years]
      .sort((left, right) => left - right)
      .map((value) => ({ label: String(value), value }));
  });

  // An override equal to the default it was made against would stop following that default once it
  // re-resolves. Dropping it keeps "at default" meaning equal to the current default, as a column
  // whose default changes under it to the user's own value then follows the next one. Values don't
  // change: only overrides equal to their column's value are dropped.
  watch(defaults, () => {
    writeOverrides(
      overridesWith({}, (column) =>
        overrides.value[column] === undefined ? undefined : valueOf(column)
      )
    );
  });

  if (config.persist) {
    const { param, scope } = config.persist;
    const { getQueryValue, parsers } = useQueryManager();
    const url = getQueryValue(param, parsers.string);

    const restore = () => {
      const stored = decodeTableFilterState(url.value, scope());
      // Text and COD columns have no options: their stored values restore as they are.
      overrides.value = overridesWith({}, (column) => {
        const storedValue = stored?.[column];
        return storedValue === undefined
          ? undefined
          : restoreStoredFilterValue(storedValue, optionsOf(column));
      });
      importedCodAgeYears.value = [];
    };

    const encoded = computed(() =>
      encodeTableFilterState(values.value, defaults.value, scope())
    );

    const writeUrl = debounce(() => {
      if (url.value !== encoded.value) url.value = encoded.value;
    }, FILTER_URL_WRITE_DELAY_MS);

    restore();
    writeUrl();
    watch(encoded, () => writeUrl());
    watch(scope, restore);
    onScopeDispose(() => writeUrl.cancel());
  }

  /** Set the given columns (e.g. a document's criteria), leaving the others alone. */
  const set = (partial: Partial<FilterState>) => {
    writeOverrides(overridesWith(overrides.value, (column) => partial[column]));
    if (partial.cod_age) {
      importedCodAgeYears.value = activeCodAgeConditions(partial.cod_age).map(
        (condition) => condition.value
      );
    }
  };

  /** Clear the given columns, defaulted ones included, leaving the others alone. */
  const clear = (columns: readonly FilterColumn[]) => {
    writeOverrides(
      overridesWith(overrides.value, (column) =>
        columns.includes(column) ? emptyFilterValue(column) : undefined
      )
    );
    if (columns.includes("cod_age")) importedCodAgeYears.value = [];
  };

  /**
   * Replace the document's filters (`null` drops them). Only the previous document's columns still
   * tagged as its own are cleared, so one the user edited stays theirs; clearing before applying
   * keeps the new document's COD years offered.
   */
  const setDocument = (next: DocumentFilterState | null) => {
    clear(summary.value.documentColumns);
    documentFilters.value = next;
    if (next) set(next.filters);
  };

  /** Put every column back to its default. */
  const reset = () => {
    overrides.value = {};
    importedCodAgeYears.value = [];
  };

  return reactive({
    options,
    defaults,
    values,
    primeFilters,
    applyPrimeFilters,
    chips,
    trailingAction,
    summary,
    codAgeYearOptions,
    set,
    clear,
    reset,
    setDocument,
  });
}

const keepIfEqual = <T>(next: T, previous: T | undefined): T =>
  previous !== undefined && isEqual(next, previous) ? previous : next;
